import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import RadioInput from '@components/application/input/radioInput';
import request from 'superagent';
import { pluralise } from '@lib/utils';
import Tooltip from '@components/application/tooltip';
import EventEmitter, { EVENT_TYPES } from '@lib/eventEmitter';

const DeliveryOptions = ({
  closeModal,
  location,
  setView,
  stockReference,
  updateDeliveryPreferences,
}) => {
  const [collectionBranches, setCollectionBranches] = useState({});
  const [selectedOptions, setSelectedOptions] = useState('');
  const [showAllBranches, setShowAllBranches] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toggleClass, setToggleClass] = useState('ch-hide');

  const requestCollectionBranches = () => {
    request
      .get(`/api/click_and_collect/centres/${stockReference}`)
      .query({
        near: location,
      })
      .then((res) => {
        if (res.status === 200) {
          setCollectionBranches(res.body);
        } else {
          setView('deliveryOptionsUnreachable');
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (window.newrelic) {
          window.newrelic.noticeError(err);
        }
        if (err.response.statusCode === 422) {
          return setView('deliveryOptionsInvalidLocation');
        }
        setView('deliveryOptionsUnreachable');
      });
  };

  const savePreference = (chosen, url) => {
    EventEmitter.emit(EVENT_TYPES.deliveryPreferenceUpdate, chosen);

    request
      .post(`/api/click_and_collect/centres`)
      .set('Content-Type', 'application/json')
      .send({
        delivery: {
          branch: chosen.branch,
          dmsid: chosen.dmsid,
          location: chosen.location,
          distance_to_customer: chosen.distance_to_customer,
          preference: chosen.preference,
        },
      })
      .then((res) => {
        if (res.status !== 200) {
          setView('deliveryOptionsUnreachable');
        }
        localStorage.setItem('postcode', chosen.location);

        if (url) window.location.href = url;
      })
      .catch((err) => {
        if (window.newrelic) {
          window.newrelic.noticeError(err);
        }
        setView('deliveryOptionsUnreachable');
      });
  };

  useEffect(() => {
    requestCollectionBranches();
  }, []);

  const renderDistanceText = (distance) => {
    const d = Math.floor(distance);
    return d === 0
      ? `Less than 1 mile away`
      : `${d} ${pluralise(d, 'mile')} away`;
  };

  const renderHoldingBranch = () => {
    const holding_branch = collectionBranches.holding_branch;

    return (
      <RadioInput
        id="delivery-branch"
        labelSubText={renderDistanceText(holding_branch.distance_to_customer)}
        labelText="Collect within 48 hours"
        name="delivery"
        onChange={() =>
          setSelectedOptions({
            holdingDmsid: holding_branch.dmsid,
            branch: holding_branch.name,
            dmsid: holding_branch.dmsid,
            distance_to_customer: holding_branch.distance_to_customer,
            location: location,
            preference: 'COLLECT_FROM_BRANCH',
          })
        }
        value={holding_branch.dmsid}
      >
        <div className="do-delivery-info ch-mb--4">
          <ul className="ch-list do-list do-list--location ch-ml--1">
            <li>{holding_branch.name}</li>
          </ul>
        </div>
      </RadioInput>
    );
  };

  const showEligibleBranches = () => {
    if (isLoading) return false;

    const closestBranchDistance =
      collectionBranches?.branches[0].distance_to_customer;
    const holdingBranchDistance =
      collectionBranches?.holding_branch.distance_to_customer;
    return (
      parseFloat(holdingBranchDistance) > parseFloat(closestBranchDistance)
    );
  };

  const renderEligibleBranches = () => {
    return collectionBranches?.branches.map((branch, index) => (
      <div className={index > 0 ? toggleClass : 'first'} key={index}>
        <RadioInput
          id={`delivery-${branch.name}`}
          labelSubText={renderDistanceText(branch.distance_to_customer)}
          labelText="Collect within 7 days"
          name="delivery"
          onChange={() =>
            setSelectedOptions({
              holdingDmsid: collectionBranches.holding_branch.dmsid,
              branch: branch.name,
              dmsid: branch.dmsid,
              distance_to_customer: branch.distance_to_customer,
              location: location,
              preference: 'CLICK_AND_COLLECT',
            })
          }
          value={branch.dmsid}
        >
          <div className="do-delivery-info ch-mb--2 ch-mt--n2">
            <ul className="ch-list do-list do-list--location ch-ml--1">
              <li>{branch.name}</li>
            </ul>
          </div>
        </RadioInput>
      </div>
    ));
  };

  const renderSkeleton = () => {
    return (
      <div className="ch-ba--1 ch-bc--grey-4 ch-rounded--sm ch-overflow--hidden ch-mb--2">
        <div className="ch-pa--2 ch-pl--8">
          <div
            className="ch-skeleton ch-mb--1 ch-width--7"
            style={{ height: '20px' }}
          />
          <div className="ch-skeleton ch-width--4" style={{ height: '15px' }} />
        </div>
        <div className="ch-bt--1 ch-bc--grey-3 ch-bg--grey-1 ch-pa--2 ch-pl--8">
          <div className="ch-skeleton" style={{ height: '21px' }} />
        </div>
      </div>
    );
  };

  const toggleMore = () => {
    if (showAllBranches) {
      setShowAllBranches(false);
      setToggleClass('ch-hide');
    } else {
      setShowAllBranches(true);
      setToggleClass('ch-show');
    }
  };

  const handleReserve = (event) => {
    event.preventDefault();
    savePreference(selectedOptions, event.target.href);
  };

  const handleClick = (event) => {
    event.preventDefault();
    savePreference(selectedOptions);
    updateDeliveryPreferences({
      branch: selectedOptions.branch,
      dmsid: selectedOptions.dmsid,
      location: selectedOptions.location,
      preference: selectedOptions.preference,
      distance_to_customer: selectedOptions.distance_to_customer,
    });
    closeModal(event);
  };

  return (
    <div className="do-section">
      <p className="do-heading">Choose your delivery option</p>
      <div className="ch-text--bold do-question">
        Collect quicker
        <Tooltip id="about-delivery" title=" ">
          <div className="ch-fs--2 ch-text--normal ac-tooltip__content">
            Ready to collect within 48 hours of order completion or when finance
            has been approved, if applicable.
          </div>
        </Tooltip>
      </div>
      {isLoading ? renderSkeleton() : renderHoldingBranch()}

      {showEligibleBranches() && (
        <>
          <div className="ch-text--bold do-question ch-mt--4">
            Closer to you
            <Tooltip id="about-delivery" title=" ">
              <div className="ch-fs--2 ch-text--normal ac-tooltip__content">
                <ul className="tooltip-list ch-mv--1 ch-lh--body">
                  <li className="ch-mb--2">
                    We can bring this vehicle to a branch closer to you.
                    Delivery costs £199 if it&apos;s up to 249 miles away, £299
                    if it&apos;s 250-399 miles away, or £399 if it&apos;s over
                    400 miles away.
                  </li>
                  <li className="ch-mb--2">
                    The fee will be added to your order.
                  </li>
                  <li>
                    We aim to deliver the vehicle within seven days of order
                    completion or when finance is approved, if applicable.
                  </li>
                </ul>
              </div>
            </Tooltip>
          </div>
          {renderEligibleBranches()}
          <div className="ch-text--center ch-mt--2 ch-mb--4">
            <button
              className="ch-btn ch-btn--link ch-fs--2"
              onClick={() => toggleMore()}
              type="button"
            >
              {showAllBranches ? 'Show fewer branches' : 'Show more branches'}
            </button>
          </div>
        </>
      )}

      {isLoading && (
        <>
          <div className="ch-text--bold do-question ch-mt--4">
            <div
              className="ch-skeleton ch-mh--auto ch-width--6"
              style={{ height: '30px' }}
            />
          </div>
          {renderSkeleton()}
          <div className="ch-text--center ch-mt--2 ch-mb--3">
            <div
              className="ch-skeleton ch-mh--auto ch-width--6"
              style={{ height: '30px' }}
            />
          </div>
        </>
      )}

      <a
        className="ch-btn ch-btn--block ch-btn--success ch-mt--5"
        disabled={selectedOptions === ''}
        href={`/reserve-now/new/${stockReference}`}
        onClick={handleReserve}
        rel="nofollow"
      >
        Reserve now for £99
      </a>

      <a
        className="ch-btn ch-btn--block ch-btn--sm"
        disabled={selectedOptions === ''}
        href="#"
        onClick={handleClick}
      >
        Confirm and return to car details
      </a>
    </div>
  );
};

export default DeliveryOptions;

DeliveryOptions.propTypes = {
  closeModal: PropTypes.func,
  location: PropTypes.string,
  setView: PropTypes.func,
  stockReference: PropTypes.string,
  updateDeliveryPreferences: PropTypes.func,
};
